import React, { useState, useContext, useMemo, useEffect, useCallback } from "react"
import { Edit, Print, CheckCircle, Cancel, AssignmentTurnedIn, Warning, Delete } from "@material-ui/icons"
import { IconButton, CircularProgress, Tooltip, Tabs, Tab, Box, Switch, FormControlLabel } from "@material-ui/core"
import BOLPrint from "components/BOL/BOLPrint"
import BOLEdit from "components/BOL/BOLEdit"
import BOLCheckout from "components/BOL/BOLCheckout"
import BOLSyncRetryModal from "components/BOL/BOLSyncRetryModal"
import BOLDelete from "components/BOL/BOLDelete"
import ErrorDialog from "components/common/ErrorDialog"
import { commas, createDateString, useLazyGqlDirect, useRestApiPost, useLazyRestApiPost, useDebounce } from "helpers"
import { queries } from "queries"
import { ReducerContext } from "App"
import MUIDataTable from "mui-datatables"
import { isEthanolTerminal, SAND_PRODUCTS, CEMENT_PRODUCTS, ETHANOL_PRODUCTS } from "constants/productTypes"

// Product category tabs configuration
const PRODUCT_CATEGORIES = {
  SAND: { label: 'Sand', filter: SAND_PRODUCTS },
  CEMENT: { label: 'Cement', filter: CEMENT_PRODUCTS },
  ETHANOL: { label: 'Ethanol', filter: ETHANOL_PRODUCTS }
}

// Map tab keys for product counts lookup
const TAB_KEYS = ['sand', 'cement', 'ethanol']

const BOLTable = ({ activePOsData, active }) => {
  const { state: { currentTerminal, user: { dbUserId } } } = useContext(ReducerContext)
  const [loadInvInter, { data: specificInvInterData, loading: specificInvInterLoading, error: specificInvInterError }] = useLazyGqlDirect(queries.GET_SPEC_INV_INTER)
  const [loadInventory, { data: inventoryData, loading: inventoryLoading, error: inventoryError }] = useLazyGqlDirect(queries.GET_SPECIFIC_TERMINAL_INVENTORY_IDS)

  const [open, setOpen] = useState(false);
  const [data, setData] = useState([])
  const [printOpen, setPrintOpen] = useState(false);
  const [printBOLData, setPrintBOLData] = useState(null);
  const [specificBolData, setSpecificBolData] = useState(null)
  const [specificPoData, setSpecificPoData] = useState(null)
  const [syncRetryOpen, setSyncRetryOpen] = useState(false)
  const [syncRetryData, setSyncRetryData] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteData, setDeleteData] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [productCounts, setProductCounts] = useState(null)
  const [searchAllProducts, setSearchAllProducts] = useState(true)

  // Tab configuration for multi-product terminals
  const showTabs = isEthanolTerminal(currentTerminal)
  const availableTabs = useMemo(() => showTabs
    ? [PRODUCT_CATEGORIES.SAND, PRODUCT_CATEGORIES.CEMENT, PRODUCT_CATEGORIES.ETHANOL]
    : [PRODUCT_CATEGORIES.SAND], [showTabs])
  const currentCategory = availableTabs[activeTab]

  const initSqlParams = useMemo(() => ({
    search_text: null,
    page_number: 0,
    sort: { column: 'created_at', direction: 'desc' },
    filter: {},
    num_per_page: 10,
    currentTerminal,
    active,
    productFilter: currentCategory.filter,
    includeProductCounts: showTabs && searchAllProducts
  }), [currentTerminal, active, currentCategory.filter, showTabs, searchAllProducts])

  const [sqlParams, setSqlParams] = useState(initSqlParams)

  const { data: BOLsData, loading: BOLsLoading, error: BOLsError } = useRestApiPost('api/bols', initSqlParams)

  const [getBOLs, { data: getBOLsData, loading: getBOLsLoading, error: getBOLsError }] = useLazyRestApiPost('api/bols')

  // Handle response format based on whether counts are included
  const processResponse = useCallback((response) => {
    if (!response) return

    // Handle new format with productCounts
    if (response.meta?.productCounts) {
      setData(response.data)
      setProductCounts(response.meta.productCounts)
    } else {
      // Legacy format (array)
      setData(response)
      setProductCounts(null)
    }
  }, [])

  useEffect(() => {
    processResponse(BOLsData)
  }, [BOLsData, processResponse])

  useEffect(() => {
    processResponse(getBOLsData)
  }, [getBOLsData, processResponse])

  const getBOLsDebounced = useDebounce(getBOLs, 1000)

  const handleClose = () => setOpen(false)
  const handlePrintClose = () => setPrintOpen(false)
  const printCheckboxHandler = ({ printCheckbox, printData }) => {
    setOpen(false)
    setPrintOpen(printCheckbox)
    setPrintBOLData(printData)
  }

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)
    const newCategory = availableTabs[newValue]
    // Preserve current search/filter when switching tabs
    const newParams = { ...sqlParams, productFilter: newCategory.filter, page_number: 0 }
    setSqlParams(newParams)
    setData([])
    getBOLs(newParams)
  }

  const handleClick = rowIndex => {
    const { bolId: bol_id, id, sand_type, purchase_order_number, external_reference_number, job_name, destination_address, transloading_client, buyer, supplier, quantity_shipped } = data[rowIndex]

    loadInvInter({ bol_id })
    loadInventory({ transloading_client, sand_type })
    setSpecificBolData(data[rowIndex])
    setSpecificPoData({ id, sand_type, purchase_order_number, external_reference_number, job_name, destination_address, transloading_client, buyer, supplier, quantity_shipped })
    setOpen(true)
  }

  const handlePrintClick = rowIndex => {
    const { bolId: bol_id } = data[rowIndex]

    loadInvInter({ bol_id })
    setPrintBOLData(data[rowIndex])
    setPrintOpen(true)
  }

  const handleSyncRetryClick = rowIndex => {
    const { bolId: bol_id } = data[rowIndex]
    loadInvInter({ bol_id })
    setSyncRetryData(data[rowIndex])
    setSyncRetryOpen(true)
  }

  const handleSyncRetryClose = () => {
    setSyncRetryOpen(false)
    setSyncRetryData(null)
  }

  const handleDeleteClick = rowIndex => {
    setDeleteData(data[rowIndex])
    setDeleteOpen(true)
  }

  const handleDeleteClose = () => {
    setDeleteOpen(false)
    setDeleteData(null)
  }

  const columns = [
    {
      name: "", options: {
        filter: false, sort: false, empty: true, display: active, customBodyRenderLite: (_, rowIndex) =>
        (<IconButton onClick={() => handleClick(rowIndex)}>
          <AssignmentTurnedIn />
        </IconButton>)
      }
    },
    {
      name: "", options: {
        filter: false, sort: false, empty: true, display: active, customBodyRenderLite: (_, rowIndex) =>
        (<IconButton onClick={() => handleDeleteClick(rowIndex)}>
          <Delete />
        </IconButton>)
      }
    },
    {
      name: "", options: {
        filter: false, sort: false, empty: true, display: !active, customBodyRenderLite: (_, rowIndex) =>
        (<IconButton onClick={() => handlePrintClick(rowIndex)}>
          <Print />
        </IconButton>)
      }
    },
    {
      name: "", options: {
        filter: false, sort: false, empty: true, display: !active, customBodyRenderLite: (_, rowIndex) =>
        (<IconButton onClick={() => handleClick(rowIndex)}>
          <Edit />
        </IconButton>)
      }
    },
    { label: "BOL ID #", name: "bolId" },
    {
      label: "Status", name: "status", options: {
        customBodyRenderLite: (_, rowIndex) => {
          const row = data[rowIndex]
          const status = row.status
          const syncStatus = row.sanddrive_sync_status
          const syncError = row.sanddrive_sync_error
          const hasSandDrive = row.source_fulfillment_id
          
          const statusIcon = (() => {
            switch (status) {
              case "started":
                return <strong>{status}</strong>
              case "completed":
                return <CheckCircle style={{ fill: "#4caf50", fontSize: "30px" }} />
              case "cancelled":
                return <Cancel style={{ fill: "rgb(239, 83, 80)", fontSize: "30px" }} />
              default:
                return null
            }
          })()
          
          // Only show sync indicator for failed SandDrive syncs (unless acknowledged)
          if (status === "completed" && hasSandDrive && syncStatus === "failed") {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {statusIcon}
                <Tooltip title={`SandDrive sync failed: ${syncError || 'Failed to sync with SandDrive'}. Click to retry or acknowledge.`}>
                  <IconButton 
                    onClick={() => handleSyncRetryClick(rowIndex)}
                    style={{ padding: 4, marginLeft: 4 }}
                  >
                    <Warning style={{ fill: "#ff9800", fontSize: "20px" }} />
                  </IconButton>
                </Tooltip>
              </div>
            )
          }
          
          // Show acknowledged state with a muted icon
          if (status === "completed" && hasSandDrive && syncStatus === "acknowledged") {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {statusIcon}
                <Tooltip title="SandDrive sync was acknowledged">
                  <Warning style={{ fill: "#ccc", fontSize: "20px", marginLeft: "8px" }} />
                </Tooltip>
              </div>
            )
          }
          
          return statusIcon
        }
      }
    },
    { label: "Driver's Name", name: "driver_name" },
    { label: "Product", name: "sand_type" },
    { label: "Trailer Number", name: "trailer_number" },
    { label: "Truck Number", name: "truck_number" },
    { label: "Carrier", name: "carrier" },
    { label: "Tare Weight", name: "tare_weight", options: { customBodyRenderLite: (_, rowIndex) => commas(data[rowIndex].tare_weight) } },
    { label: "External Reference Number", name: "external_reference_number" },
    { label: "Job Name", name: "job_name" },
    { label: "Created At", name: "created_at", options: { customBodyRenderLite: (_, rowIndex) => createDateString(data[rowIndex].created_at) } }
  ]

  const col_names = columns.map(col => col.name)

  const options = {
    selectableRows: 'none',
    searchAlwaysOpen: true,
    print: false,
    download: false,
    viewColumns: false,
    filterType: 'textField',
    serverSide: true,
    page: sqlParams.page_number,
    count: data[0] ? Number(data[0].total_rows) : 0,
    rowsPerPage: sqlParams.num_per_page,
    rowsPerPageOptions: [10, 25, 50, 100],
    customToolbar: () => <>{(getBOLsLoading || BOLsLoading) && <CircularProgress />}</>,
    onChangePage: page_number => {
      setSqlParams({ ...sqlParams, page_number })
      getBOLs({ ...sqlParams, page_number, currentTerminal })
    },
    onChangeRowsPerPage: num_per_page => {
      setSqlParams({ ...sqlParams, num_per_page })
      getBOLs({ ...sqlParams, num_per_page, currentTerminal })
    },
    onColumnSortChange: (column, direction) => {
      setSqlParams({ ...sqlParams, sort: { column, direction }, page_number: 0 })
      getBOLs({ ...sqlParams, sort: { column, direction }, page_number: 0, currentTerminal })
    },
    onFilterChange: (_, filterList) => {
      setSqlParams({ ...sqlParams, filter: Object.fromEntries(filterList.flatMap((item, ind) => item[0] ? [[col_names[ind], item[0]]] : [])), page_number: 0 })
      getBOLsDebounced({ ...sqlParams, currentTerminal, filter: Object.fromEntries(filterList.flatMap((item, ind) => item[0] ? [[col_names[ind], item[0]]] : [])), page_number: 0 })
    },
    onSearchChange: search_text => {
      setSqlParams({ ...sqlParams, search_text, page_number: 0 })
      getBOLsDebounced({ ...sqlParams, currentTerminal, search_text, page_number: 0 })
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const dataFormatted = data.map(item => {
        const [, ...data] = item.data
        return { ...item, data }
      })

      return buildHead(columns.filter(item => item.label !== 'Actions')) + buildBody(dataFormatted)
    }
  }

  return (
    <>
      {(BOLsError || getBOLsError) && <ErrorDialog error={BOLsError || getBOLsError} />}

      {/* Product category tabs - only show if multiple categories available (Binghamton) */}
      {showTabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="BOL category tabs">
            {availableTabs.map((category, index) => {
              const count = searchAllProducts ? productCounts?.[TAB_KEYS[index]] : undefined
              const label = count !== undefined ? `${category.label} (${count})` : category.label
              return <Tab key={category.label} label={label} id={`bol-tab-${index}`} />
            })}
          </Tabs>
          <FormControlLabel
            control={
              <Switch
                checked={searchAllProducts}
                onChange={e => {
                  setSearchAllProducts(e.target.checked)
                  setProductCounts(null)
                  const newParams = { ...sqlParams, includeProductCounts: e.target.checked, page_number: 0 }
                  setSqlParams(newParams)
                  getBOLs(newParams)
                }}
                color="primary"
                size="small"
              />
            }
            label="Search all products"
            style={{ marginRight: 8 }}
          />
        </Box>
      )}

      <MUIDataTable title={<CircularProgress />} {...{ data, columns, options }} />

      {(specificInvInterLoading || inventoryLoading) && <CircularProgress />}
      {(specificInvInterError || inventoryError) && <ErrorDialog error={specificInvInterError || inventoryError} />}
      {open && (active ?
        <BOLCheckout
          invInter={specificInvInterData ? specificInvInterData.invInters : []}
          inventoryData={inventoryData ? inventoryData.termInv.filter(inv => inv.terminal === currentTerminal) : []}
          specificBolData={specificBolData}
          specificPoData={specificPoData}
          open={open}
          handleClose={handleClose}
          activePOsData={activePOsData}
          loadInvInter={loadInvInter}
          loadInventory={loadInventory}
          printCheckboxHandler={printCheckboxHandler}
          getBOLs={getBOLs}
          sqlParams={sqlParams}
          specificInvInterLoading={specificInvInterLoading}
        />
        :
        <BOLEdit
          invInter={specificInvInterData ? specificInvInterData.invInters : []}
          getBOLs={getBOLs}
          sqlParams={sqlParams}
          open={open}
          handleClose={handleClose}
          specificBolData={specificBolData}
        />)}
      {(printOpen && printBOLData && specificInvInterData) &&
        <BOLPrint
          bolData={printBOLData}
          handleClose={handlePrintClose}
          open={printOpen}
          specificInvInterData={specificInvInterData}
        />}
      {syncRetryOpen && syncRetryData && specificInvInterData &&
        <BOLSyncRetryModal
          open={syncRetryOpen}
          handleClose={handleSyncRetryClose}
          bolData={syncRetryData}
          invInterData={specificInvInterData.invInters}
          getBOLs={getBOLs}
          sqlParams={sqlParams}
          currentTerminal={currentTerminal}
          dbUserId={dbUserId}
        />}
      {deleteOpen && deleteData &&
        <BOLDelete
          open={deleteOpen}
          handleClose={handleDeleteClose}
          bolData={deleteData}
          getBOLs={getBOLs}
          sqlParams={sqlParams}
          currentTerminal={currentTerminal}
        />}
    </>
  )
}

export default BOLTable;
